/**
 * BADGES SCREEN — matches mockup: badge grid, T-shirt reward card,
 * earned vs locked sections, share button
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { roadsAPI, authAPI, venuesAPI } from '../api';
import { T, fonts } from '../theme';
import LoadingSpinner from '../components/LoadingSpinner';
import BottomNav from '../components/BottomNav';
import VenueModal from '../components/VenueModal';

function ProgressBar({ pct }) {
  return (
    <div style={{ padding: '0 20px' }}>
      <div style={{ height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: T.gold, borderRadius: 10, transition: 'width 0.8s ease' }} />
      </div>
      <div style={{ fontSize: 11, color: T.smoke, textAlign: 'center', marginTop: 8, letterSpacing: 0.5 }}>{pct}% complete</div>
    </div>
  );
}

function BadgeCell({ venue, earned, onClick }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer', opacity: earned ? 1 : 0.8 }} onClick={onClick}>
      <div style={{ 
        width: 100, height: 100, borderRadius: '50%', 
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40, 
        position: 'relative', 
        background: earned ? `linear-gradient(180deg, ${T.gold} 0%, ${T.copperDark} 100%)` : '#EAEAEA',
        boxShadow: earned ? '0 8px 20px rgba(184,115,58,0.3)' : 'inset 0 2px 4px rgba(0,0,0,0.05)'
      }}>
        {venue.icon || '🥃'}
        {earned && (
          <div style={{ 
            position: 'absolute', bottom: 4, right: 4, width: 22, height: 22, 
            borderRadius: '50%', background: T.success, border: '2.5px solid white', 
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'white' 
          }}>✓</div>
        )}
      </div>
      <div style={{ 
        fontSize: 10, color: earned ? '#1a1a1a' : T.smoke, textAlign: 'center', 
        lineHeight: 1.3, fontWeight: '700', padding: '0 4px', marginTop: 4
      }}>
        {venue.name}
      </div>
      {earned && <div style={{ fontSize: 10, color: T.gold, fontWeight: '600', marginTop: -2 }}>Tap for details</div>}
    </div>
  );
}

export default function Badges() {
  const { user } = useAuth();
  const [roads, setRoads] = useState([]);
  const [selectedRoad, setSelectedRoad] = useState(null);
  const [venues, setVenues] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadInitial = async () => {
        try {
            const [roadsRes, checkinsRes] = await Promise.all([
                roadsAPI.getAll(),
                venuesAPI.getUserCheckins()
            ]);
            
            setRoads(roadsRes.data);
            setCheckins(checkinsRes.data);
            
            const savedSlug = localStorage.getItem('selectedRoadSlug');
            const initialRoad = savedSlug 
                ? roadsRes.data.find(r => r.slug === savedSlug) || roadsRes.data[0]
                : roadsRes.data[0];
            
            if (initialRoad) setSelectedRoad(initialRoad);
        } catch (err) {
            console.error('Failed to load initial data:', err);
        } finally {
            setLoading(false);
        }
    };
    loadInitial();
  }, []);

  useEffect(() => {
    if (!selectedRoad) return;
    setLoading(true);
    venuesAPI.getAll(selectedRoad.slug)
        .then(res => setVenues(res.data))
        .catch(console.error)
        .finally(() => setLoading(false));
  }, [selectedRoad]);

  const checkedInIds = new Set(checkins.map(c => c.venue_id));
  const earned = venues.filter(v => checkedInIds.has(v.id));
  const locked = venues.filter(v => !checkedInIds.has(v.id));
  const stopsRequired = selectedRoad?.stops_required || venues.length || 0;
  const pct = stopsRequired > 0 ? Math.min(100, Math.round((earned.length / stopsRequired) * 100)) : 0;
  
  const handleClaim = () => {
      if (pct < 100) return;
      // Navigate to profile to check/set t-shirt size if not set
      window.location.href = '/profile';
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div style={{ background: '#F4F1EA', minHeight: '100vh', paddingBottom: 100, fontFamily: fonts.body }}>
      {/* Header */}
      <div style={{ background: T.ink, padding: '50px 20px 30px', textAlign: 'center', borderBottomLeftRadius: 24, borderBottomRightRadius: 24 }}>
        <h1 style={{ fontFamily: fonts.heading, fontSize: 34, fontWeight: 900, color: 'white', margin: '0 0 10px' }}>My Badges</h1>
        
        {/* Road Selector Carousel */}
        <div style={{ display: 'flex', gap: 10, overflowX: 'auto', padding: '10px 0 20px', scrollbarWidth: 'none', justifyContent: 'center' }}>
            {roads.map(r => (
                <div 
                    key={r.id} 
                    onClick={() => {
                        setSelectedRoad(r);
                        localStorage.setItem('selectedRoadSlug', r.slug);
                    }}
                    style={{ 
                        padding: '6px 14px', borderRadius: 20, fontSize: 11, fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s',
                        background: selectedRoad?.id === r.id ? T.gold : 'rgba(255,255,255,0.1)',
                        color: selectedRoad?.id === r.id ? T.ink : '#8899aa',
                        border: selectedRoad?.id === r.id ? `1px solid ${T.gold}` : '1px solid transparent'
                    }}
                >
                    {r.name}
                </div>
            ))}
        </div>

        <div style={{ fontSize: 14, color: '#7A8F96', marginBottom: 24, letterSpacing: 0.2 }}>
          {earned.length} of {stopsRequired} earned on {selectedRoad?.name || 'The Copper Road'}
        </div>
        <ProgressBar pct={pct} />
      </div>

      {/* T-Shirt Reward Pill Card */}
      <div 
        onClick={handleClaim}
        style={{ 
            margin: '24px 20px', padding: '16px 24px', 
            background: T.ink, borderRadius: 30, 
            border: `1.5px solid ${pct === 100 ? T.success : '#333'}`, 
            display: 'flex', alignItems: 'center', gap: 16,
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            cursor: pct === 100 ? 'pointer' : 'default'
        }}
      >
        <div style={{ fontSize: 40, background: '#1C3040', padding: 8, borderRadius: 12 }}>👕</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: '700', color: 'white', letterSpacing: -0.2 }}>
            Copper Road T-Shirt Reward
          </div>
          <div style={{ fontSize: 13, color: pct === 100 ? T.success : '#7A8F96', marginTop: 4, fontWeight: pct === 100 ? '700' : '400' }}>
            {pct === 100 ? 'TAP HERE TO CLAIM REWARD!' : `Complete ${stopsRequired} stops to claim yours`}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 26, fontWeight: '900', color: pct === 100 ? T.success : T.gold, fontFamily: fonts.heading }}>{earned.length}</div>
          <div style={{ fontSize: 9, color: '#7A8F96', fontWeight: '800', letterSpacing: 1.5 }}>OF {stopsRequired}</div>
        </div>
      </div>

      {/* Earned Section */}
      {earned.length > 0 && (
        <div style={{ padding: '0 20px 24px' }}>
          <div style={{ fontSize: 11, letterSpacing: 1.5, fontWeight: '800', color: T.smoke, marginBottom: 20 }}>EARNED ✓</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24 }}>
            {earned.map(v => <BadgeCell key={v.id} venue={v} earned onClick={() => setSelectedVenue(v)} />)}
          </div>
        </div>
      )}

      {/* Locked Section */}
      {locked.length > 0 && (
        <div style={{ padding: '0 20px 40px' }}>
          <div style={{ fontSize: 11, letterSpacing: 1.5, fontWeight: '800', color: T.smoke, marginBottom: 20 }}>LOCKED — {locked.length} REMAINING</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
            {locked.map(v => <BadgeCell key={v.id} venue={v} earned={false} onClick={() => setSelectedVenue(v)} />)}
          </div>
        </div>
      )}

      {selectedVenue && (
        <VenueModal 
            venue={selectedVenue} 
            isCheckedIn={checkedInIds.has(selectedVenue.id)} 
            checkinAt={checkins.find(c => c.venue_id === selectedVenue.id)?.checkin_at}
            onClose={() => setSelectedVenue(null)} 
            onScanQR={() => navigate('/checkin')}
        />
      )}
      <BottomNav active="/badges" />
    </div>
  );
}
